import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { ORGANIZATION_ENDPOINTS } from './organization.endpoints';
import { DataTableQuery, DataTableResult } from '../../../../../shared/components/data-table/data-table.types';
import { dataTableRequest } from '../../../../../shared/components/data-table/data-table-request';
import {
    OrganizationDetailsDto,
    OrganizationGridFilters,
    OrganizationHistoryEntryDto,
    OrganizationUsageDto,
    OrganizationDto,
    OrganizationLookupDto,
    OrganizationTableRow,
    RecentOrganizationsDashboardDto
} from '../models/organization.model';

@Injectable({
    providedIn: 'root'
})
export class OrganizationService {
    private readonly http = inject(HttpClient);

    getRecentOrganizationsDashboard(months?: number, limit?: number): Observable<RecentOrganizationsDashboardDto> {
        let params = new HttpParams();
        if (months !== undefined && months !== null) {
            params = params.set('months', months.toString());
        }
        if (limit !== undefined && limit !== null) {
            params = params.set('limit', limit.toString());
        }
        return this.http.get<RecentOrganizationsDashboardDto>(ORGANIZATION_ENDPOINTS.dashboard, { params });
    }

    getOrganizationDataTable(query: DataTableQuery, filters: OrganizationGridFilters = {}): Observable<DataTableResult<OrganizationDto>> {
        // TenantsController.DataTable binds its filters [FromQuery]; in the body they were silently ignored.
        let params = new HttpParams();
        const set = (key: string, value: string | null | undefined): void => {
            const trimmed = value?.trim();
            if (trimmed) params = params.set(key, trimmed);
        };
        set('code', filters.code);
        set('name', filters.name);
        set('countryId', filters.countryId);
        set('planId', filters.planId);
        set('statuses', filters.status);
        set('subscription', filters.subscription);
        const request = dataTableRequest(query, params);
        return this.http.post<DataTableResult<OrganizationDto>>(ORGANIZATION_ENDPOINTS.dataTable, request.body, { params: request.params });
    }

    /** Emails the organization's first administrator a new set-password link (the old one stops working). */
    resendInvitation(id: string): Observable<{ adminEmail: string; invitationSent: boolean; invitationExpiresOn?: string | null }> {
        return this.http.post<{ adminEmail: string; invitationSent: boolean; invitationExpiresOn?: string | null }>(
            ORGANIZATION_ENDPOINTS.resendInvitation(id), {});
    }

    /** Emails the organization's administrator a password reset link. */
    resetAdminPassword(id: string): Observable<{ adminEmail: string }> {
        return this.http.post<{ adminEmail: string }>(ORGANIZATION_ENDPOINTS.resetAdminPassword(id), {});
    }

    /** Deletes the organization; the server checks the typed name and refuses protected organizations. */
    deleteOrganization(id: string, confirmName: string): Observable<{ id: string; code: string; name: string; usersReleased: number }> {
        return this.http.post<{ id: string; code: string; name: string; usersReleased: number }>(
            ORGANIZATION_ENDPOINTS.deleteOrganization(id), { confirmName });
    }

    getOrganizationDetails(id: string): Observable<OrganizationDetailsDto> {
        return this.http.get<OrganizationDetailsDto>(ORGANIZATION_ENDPOINTS.organizationDetails(id));
    }

    getOrganizationHistory(id: string): Observable<OrganizationHistoryEntryDto[]> {
        return this.http.get<OrganizationHistoryEntryDto[]>(ORGANIZATION_ENDPOINTS.organizationHistory(id));
    }

    getOrganizationUsage(id: string): Observable<OrganizationUsageDto> {
        return this.http.get<OrganizationUsageDto>(ORGANIZATION_ENDPOINTS.organizationUsage(id));
    }

    getOrganizationById(id: string): Observable<OrganizationDto> {
        return this.http.get<OrganizationDto>(ORGANIZATION_ENDPOINTS.getById(id));
    }

    autocomplete(term?: string, limit = 10): Observable<readonly OrganizationLookupDto[]> {
        let params = new HttpParams().set('limit', limit);
        const normalizedTerm = term?.trim();

        if (normalizedTerm) {
            params = params.set('term', normalizedTerm);
        }

        return this.http.get<readonly OrganizationLookupDto[]>(ORGANIZATION_ENDPOINTS.autocomplete, { params });
    }
}
